import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';
import { Subject, combineLatest, of } from 'rxjs';
import { filter, switchMap, takeUntil, map, catchError, take } from 'rxjs/operators';
import { CustomerService } from '../../services/customer.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

@Component({
	selector: 'app-pending-auths',
	templateUrl: './pending-auths.component.html',
	styleUrls: ['./pending-auths.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({ transform: 'rotate3d(0, 1, 0, 179deg)' })),
			state('inactive', style({ transform: 'rotateY(0)' })),
			transition('active => inactive', animate('500ms ease-out')),
			transition('inactive => active', animate('500ms ease-in')),
		]),
		trigger('grow', [
			transition(':enter', [
				style({ height: '0', width: '0' }),
				animate(200, style({ height: '*', width: '*' })),
				animate('200ms', style({ opacity: 1 })),
			]),
			transition(':leave', [
				animate(100, style({ height: 0, width: 0 })),
				animate('100ms', style({ opacity: 0 })),
			]),
		]),
	]
})
export class PendingAuthsComponent implements OnInit, OnDestroy {
	@Output() viewData = new EventEmitter<any>();

	flip = 'inactive';
	preauthEnabled = false;
	items: any[] = [];
	expiringSoonCount = 0;
	isLoading = true;
	viewing: any = false;
	title = 'Pending Auths';
	config: any = null;
	actionBusy: Record<string, boolean> = {};
	voidFeedback: { type: 'success' | 'error'; text: string } | null = null;

	private readonly destroy$ = new Subject<void>();
	private voidFeedbackClearTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		private customerService: CustomerService,
		private globalSearchService: GlobalSearchService
	) {}

	ngOnInit(): void {
		combineLatest([
			this.globalSearchService.configsubscription,
			this.globalSearchService.user,
		])
			.pipe(
				filter(
					([cfg, user]) =>
						!!cfg?.apiServer?.endpoint &&
						!!cfg?.apiCredentials?.clientId &&
						!!user &&
						!Array.isArray(user) &&
						!!user.user?.userid
				),
				switchMap(([cfg]) => {
					this.config = cfg;
					if (!cfg.arpreauth) {
						return of({
							preauth_enabled: false,
							items: [] as any[],
							_skipped: true as const,
						});
					}
					return this.customerService.getPendingAuths().pipe(
						map((res) => ({ ...res, _skipped: false as const })),
						catchError(() =>
							of({
								preauth_enabled: false,
								items: [] as any[],
								_skipped: false as const,
							})
						)
					);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: (res: any) => {
					if (res._skipped) {
						this.preauthEnabled = false;
						this.items = [];
						this.expiringSoonCount = 0;
						this.isLoading = false;
						return;
					}
					this.preauthEnabled = !!res.preauth_enabled;
					this.items = Array.isArray(res.items) ? res.items : [];
					this.expiringSoonCount = this.items.filter((i: any) => i.expiring_soon).length;
					this.isLoading = false;
				},
			});
	}

	ngOnDestroy(): void {
		if (this.voidFeedbackClearTimer) {
			clearTimeout(this.voidFeedbackClearTimer);
			this.voidFeedbackClearTimer = null;
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	toggleFlip(open: boolean) {
		if (!open) {
			this.flip = 'inactive';
			this.viewData.emit(false);
			return;
		}
		this.flip = this.flip === 'active' ? 'inactive' : 'active';
		if (this.flip === 'active') {
			this.viewing = this.items;
			this.viewData.emit({ data: this.items, title: 'Pending Auths' });
		} else {
			this.viewData.emit(false);
		}
	}

	rowKey(a: any): string {
		return a?.auth_id || a?.transid || '';
	}

	reloadList(): void {
		if (!this.config?.arpreauth) {
			return;
		}
		this.isLoading = true;
		this.customerService
			.getPendingAuths()
			.pipe(
				take(1),
				catchError(() => of({ preauth_enabled: true, items: [] as any[] }))
			)
			.subscribe((res: any) => {
				this.preauthEnabled = !!res.preauth_enabled;
				this.items = Array.isArray(res.items) ? res.items : [];
				this.expiringSoonCount = this.items.filter((i: any) => i.expiring_soon).length;
				if (this.flip === 'active') {
					this.viewing = this.items;
					this.viewData.emit({ data: this.items, title: 'Pending Auths' });
				}
				this.isLoading = false;
			});
	}

	voidAuth(a: any, ev: Event): void {
		ev.preventDefault();
		ev.stopPropagation();
		const key = this.rowKey(a);
		if (!key || this.actionBusy[key]) {
			return;
		}
		if (!confirm('Void this card authorization? The hold will be released at the processor.')) {
			return;
		}
		this.actionBusy[key] = true;
		this.voidFeedback = null;
		const payload = a.auth_id ? { auth_id: a.auth_id } : { transid: a.transid };
		this.customerService.voidPendingAuth(payload).subscribe({
			next: (r: any) => {
				this.actionBusy[key] = false;
				const msg = (r?.message && String(r.message).trim()) || (r?.success ? 'Authorization voided.' : 'Void failed.');
				if (r?.success) {
					this.voidFeedback = { type: 'success', text: msg };
					this.reloadList();
				} else {
					this.voidFeedback = { type: 'error', text: msg };
				}
				this.scheduleClearVoidFeedback();
			},
			error: (err: any) => {
				this.actionBusy[key] = false;
				const msg =
					err?.error?.message ||
					err?.error?.Message ||
					err?.message ||
					'Void request failed.';
				this.voidFeedback = { type: 'error', text: typeof msg === 'string' ? msg : 'Void request failed.' };
				this.scheduleClearVoidFeedback();
			},
		});
	}

	private scheduleClearVoidFeedback(): void {
		if (this.voidFeedbackClearTimer) {
			clearTimeout(this.voidFeedbackClearTimer);
		}
		this.voidFeedbackClearTimer = setTimeout(() => {
			this.voidFeedback = null;
			this.voidFeedbackClearTimer = null;
		}, 8000);
	}
}
